<template>
    <template v-if="isFormType(FORM_TYPE.TRAVEL_GUARANTEE)">
        <travel-guarantee-issue-form
            id="travel-form"
            v-model:form-type="formType"
            :types="types"
            :type-classifier="classifiers[CLASSIFIERS.COMPENSATION_TYPE]"
            :cause-classifier="classifiers[CLASSIFIERS.DEMAND_CATEGORY]"
            :compensation-variant="compensation"
            :is-loading="isLoading"
            @cancel="handleCancel"
        />
    </template>
    <template v-else-if="isFormType(FORM_TYPE.REFUND)">
        <refund-issue-form
            id="refund-form"
            v-model:form-type="formType"
            :types="types"
            :refund-reason-classifier="classifiers[CLASSIFIERS.REFUND_REASON]"
            :refund-source-classifier="classifiers[CLASSIFIERS.REFUND_SOURCE]"
            :compensation-variant="compensation"
            :is-loading="isLoading"
            @cancel="handleCancel"
        />
    </template>
    <template v-else-if="isFormType(FORM_TYPE.ORDER_TIMETABLE)">
        <order-timetable-issue-form
            id="time-form"
            v-model:form-type="formType"
            :types="types"
            :bus-route-classifier="classifiers[CLASSIFIERS.BUS_ROUTE]"
            :boat-route-classifier="classifiers[CLASSIFIERS.BOAT_ROUTE]"
            :is-loading="isLoading"
            @cancel="handleCancel"
        />
    </template>
    <template v-else>
        <basic-issue-form
            id="form"
            v-model:form-type="formType"
            :types="types"
            :transport-type="classifiers[CLASSIFIERS.TRANSPORT_TYPE]"
            :categories="sortedCategories"
            :is-loading="isLoading"
            @cancel="handleCancel"
        />
    </template>
</template>

<script setup lang="ts">
import { ref, watch, defineAsyncComponent, computed } from 'vue';
import { debounce } from 'lodash-es';
import { getClassifiers, getIssueCategories, getIssueTypes } from '@/features/Issues/services/api';
import { CLASSIFIERS } from '@/features/Issues/constants';
import { useMainStore } from '@/features/common/stores';
import { FORM_TYPE } from '../../../constants';
import { DEBOUNCE_DELAY } from '@/constants';
import { useAppConfiguration } from '@/composables';
import { mapClassifierData, mapKeyValueData } from '@/features/Issues/helpers';
import { Classifiers, KeyValueDto } from '@/features/Issues/types';
import { useRouter } from 'vue-router';

const BasicIssueForm = defineAsyncComponent(() => import('../components/default/form.vue'));
const RefundIssueForm = defineAsyncComponent(() => import('../components/refund/form.vue'));
const TravelGuaranteeIssueForm = defineAsyncComponent(() => import('../components/travelGuarantee/form.vue'));
const OrderTimetableIssueForm = defineAsyncComponent(() => import('../components/orderTimetable/form.vue'));

const mainStore = useMainStore();
const { language } = useAppConfiguration();
const router = useRouter();

interface Props {
    initialFormType?: string;
}

const props = defineProps<Props>();

const formType = ref<KeyValueDto | undefined>();
const categories = ref<KeyValueDto[]>([]);
const sortedCategories = computed(() => [...categories.value].sort((a, b) => a.value.localeCompare(b.value)));
const types = ref<KeyValueDto[]>([]);
const classifiers = ref<Classifiers>({});
const isLoading = ref(false);
const compensation = ref<Record<string, string>>({});

const initializeFormType = () => {
    const formTypeKey = props.initialFormType;

    if (formTypeKey) {
        // Case-insensitive check against FORM_TYPE enum values
        const normalizedKey = formTypeKey.toUpperCase();
        const validFormType = Object.values(FORM_TYPE).find(ft => ft.toUpperCase() === normalizedKey);

        if (validFormType) {
            // Find matching type using the valid form type (preserves correct casing)
            const matchingType = types.value.find(t => t.key.toUpperCase() === validFormType.toUpperCase());
            if (matchingType) {
                formType.value = matchingType;
            }
        }
    }
};

const getIssueFormData = async () => {
    try {
        isLoading.value = true;
        const [typesData, categoriesData, classifiersData] = await Promise.all([
            getIssueTypes(),
            getIssueCategories(),
            getClassifiers(Object.values(CLASSIFIERS).join(','))
        ]);

        types.value = typesData?.valueList || [];
        categories.value = categoriesData?.valueList || [];
        classifiers.value = mapClassifierData(classifiersData);
        compensation.value = mapKeyValueData(classifiers.value[CLASSIFIERS.COMPENSATION_VARIANT]);

        initializeFormType();
    } catch (error) {
        mainStore.registerError(error);
    } finally {
        isLoading.value = false;
    }
};

const debouncedGetIssueFormData = debounce(getIssueFormData, DEBOUNCE_DELAY);

watch(
    language,
    () => {
        debouncedGetIssueFormData();
    },
    { immediate: true }
);

const isFormType = (type: string): boolean => formType.value?.key === type && Object.values(FORM_TYPE).includes(formType.value.key);

const handleCancel = () => {
    // go back to the previous page
    router.back();
};
</script>
